/**
 * チバリヨ - P4 CSV インポート機能 (Phase 2-A-3)
 *
 * 256社の本番投入のための CSV 一括取り込みモジュール。
 *
 * 提供するもの:
 *   - openCsvImportModal({ existingClients, members, currentSessionMember, onComplete })
 *       P4 画面から呼び出してモーダルを開く
 *
 * 設計メモ:
 *   - 3ステップウィザード(ファイル選択 → プレビュー → 投入結果)
 *   - 列名固定(揺らぎ吸収はしない、テンプレ CSV を配布)
 *   - 文字コードは UTF-8 / UTF-8 BOM / Shift_JIS を自動判定
 *   - 区切りはカンマ固定(タブ・セミコロンは弾く)
 *   - 検証は エラー(投入不可) / 警告(投入はする) / 重複(ユーザー選択) の3段階
 *   - 投入は Firestore writeBatch で一括(256社なら1バッチ500件以内に収まる)
 *   - 失敗時は失敗行 CSV のダウンロードを提供
 *
 * 依存:
 *   - p4-clients.js から呼ばれる(独立モジュールとして切り出し)
 *   - normalizeClientName / generateArea のロジックは p4-clients.js と統一させるため
 *     本ファイル内で同一実装を保持(将来 utils 切り出し時にまとめる)
 */

import {
  doc,
  writeBatch,
  serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { db } from './firebase-init.js';

// =====================================================================
// 定数
// =====================================================================

const BUSINESS_TYPES = ['物流機器', 'ベジコン', 'その他'];

const CSV_HEADERS = [
  '顧客名',
  'カナ',
  '都道府県',
  '市町村',
  '提案種別',
  '主担当',
  'カテゴリ',
  '備考'
];

const REQUIRED_HEADERS = ['顧客名'];

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;  // 5MB

const FIRESTORE_BATCH_LIMIT = 500;

// 47都道府県のフォールバック一覧(municipalities.json が読めなくても DP 列検証が動くように)
const PREFECTURES_FALLBACK = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
];

// =====================================================================
// モジュールスコープの状態
// =====================================================================

let modalState = null;  // { existingClients, members, currentSessionMember, onComplete, ... }

// =====================================================================
// 公開関数:モーダルを開く
// =====================================================================
export function openCsvImportModal(options) {
  const {
    existingClients = [],
    members = new Map(),
    currentSessionMember = null,
    municipalitiesData = null,
    onComplete = null
  } = options || {};

  // 既存モーダルがあれば閉じる(多重起動防止)
  closeModal();

  modalState = {
    existingClients,
    members,
    currentSessionMember,
    municipalitiesData,
    onComplete,
    // 進行中の状態
    step: 1,
    file: null,
    fileError: null,
    parsedRows: [],     // [{ rowNum, raw, parsed, issues, status, action, targetClientId }]
    duplicatePolicy: 'individual',  // 'skip_all' | 'overwrite_all' | 'individual'
    // 投入結果
    importResult: null
  };

  mountModal();
  renderStep1();
}

// =====================================================================
// モーダル DOM のマウント
// =====================================================================
function mountModal() {
  // 既存があれば消す
  const existing = document.getElementById('p4-csv-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'p4-csv-modal';
  modal.className = 'p4-csv-modal';
  modal.innerHTML = `
    <div class="p4-csv-modal-inner" role="dialog" aria-labelledby="p4-csv-title">
      <div class="p4-csv-header">
        <h2 id="p4-csv-title" class="p4-csv-title">CSV インポート</h2>
        <button type="button" class="p4-csv-close" id="p4-csv-close-btn" aria-label="閉じる">×</button>
      </div>
      <div class="p4-csv-steps" id="p4-csv-steps"></div>
      <div class="p4-csv-body" id="p4-csv-body"></div>
      <div class="p4-csv-footer" id="p4-csv-footer"></div>
    </div>
  `;
  document.body.appendChild(modal);

  document.getElementById('p4-csv-close-btn').addEventListener('click', () => {
    handleCloseRequest();
  });

  // 背景クリックで閉じる(Step 3 投入中以外)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      handleCloseRequest();
    }
  });

  // Esc で閉じる
  document.addEventListener('keydown', escHandler);
}

function escHandler(e) {
  if (e.key === 'Escape' && document.getElementById('p4-csv-modal')) {
    handleCloseRequest();
  }
}

function handleCloseRequest() {
  if (!modalState) return;
  // 投入中(Step 3 進行中)は閉じさせない
  if (modalState.step === 3 && !modalState.importResult) {
    return;
  }
  // 投入完了済みなら即閉じる、その他は確認
  if (modalState.step === 2 && modalState.parsedRows.length > 0) {
    if (!window.confirm('CSV インポートを中止します。よろしいですか?')) {
      return;
    }
  }
  closeModal();
}

function closeModal() {
  const m = document.getElementById('p4-csv-modal');
  if (m) m.remove();
  document.removeEventListener('keydown', escHandler);
  modalState = null;
}

// =====================================================================
// ステップインジケータの描画
//   - 通常: 現在ステップは is-active(青)、それ以前は is-done(緑)
//   - Step 3 は最終ステップなので、投入完了かつ失敗0件のときに限り is-done 化
//     失敗ありのときは is-active のまま(完了感を出さない)
// =====================================================================
function renderSteps() {
  const wrap = document.getElementById('p4-csv-steps');
  if (!wrap) return;
  const labels = ['ファイル選択', 'プレビュー & 検証', '投入'];
  const isStep3Complete = (
    modalState.step === 3
    && modalState.importResult
    && modalState.importResult.failed === 0
  );
  const html = labels.map((label, i) => {
    const num = i + 1;
    let cls = '';
    if (num < modalState.step) cls = 'is-done';
    else if (num === modalState.step) {
      cls = (num === 3 && isStep3Complete) ? 'is-done' : 'is-active';
    }
    const divider = i < labels.length - 1 ? '<div class="p4-csv-step-divider"></div>' : '';
    return `
      <div class="p4-csv-step ${cls}">
        <span class="p4-csv-step-num">${num}</span>
        <span>${label}</span>
      </div>
      ${divider}
    `;
  }).join('');
  wrap.innerHTML = html;
}

// =====================================================================
// Step 1: ファイル選択
// =====================================================================
function renderStep1() {
  modalState.step = 1;
  modalState.parsedRows = [];
  modalState.fileError = null;
  renderSteps();

  const body = document.getElementById('p4-csv-body');
  body.innerHTML = `
    <div class="p4-csv-step1">
      <label class="p4-csv-dropzone ${modalState.file ? 'has-file' : ''}" id="p4-csv-dropzone">
        <div class="p4-csv-dropzone-icon">📄</div>
        <div class="p4-csv-dropzone-text">
          CSV をここにドラッグ&ドロップ
        </div>
        <div class="p4-csv-dropzone-sub">
          またはクリックしてファイルを選択 (.csv, 最大 5MB)
        </div>
        <input type="file" id="p4-csv-file-input" accept=".csv,text/csv">
      </label>

      <div id="p4-csv-file-info-wrap"></div>

      <div>
        <a href="#" id="p4-csv-template-link" class="p4-csv-template-link">
          📥 テンプレート CSV をダウンロード
        </a>
      </div>

      <details class="p4-csv-format-details">
        <summary>CSV フォーマットの仕様</summary>
        <div class="p4-csv-format-content">
          <p>
            列名は固定です(列順は任意・未知の列は無視されます)。<br>
            文字コードは <code>UTF-8</code>(BOM付/無し)または <code>Shift_JIS</code> に対応します。
          </p>
          <table>
            <thead>
              <tr>
                <th>列名</th>
                <th>必須</th>
                <th>検証</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>顧客名</td><td>★</td><td>1〜100文字</td></tr>
              <tr><td>カナ</td><td></td><td>〜100文字、非カタカナで警告</td></tr>
              <tr><td>都道府県</td><td></td><td>47都道府県のいずれか(警告のみ)</td></tr>
              <tr><td>市町村</td><td></td><td>市町村マスタに存在(警告のみ)</td></tr>
              <tr><td>提案種別</td><td></td><td>カンマ区切り、<code>物流機器/ベジコン/その他</code></td></tr>
              <tr><td>主担当</td><td></td><td>メンバー名と一致(警告のみ)</td></tr>
              <tr><td>カテゴリ</td><td></td><td>〜50文字</td></tr>
              <tr><td>備考</td><td></td><td>〜1000文字</td></tr>
            </tbody>
          </table>
          <p style="margin-top:8px;">
            提案種別は1セル内でカンマ区切り(例:<code>"物流機器,ベジコン"</code>)。<br>
            空欄可の列も多いですが、顧客名のみは必須です。
          </p>
        </div>
      </details>

      ${modalState.fileError ? `
        <div class="p4-csv-step-error">
          ${escapeHtml(modalState.fileError)}
        </div>
      ` : ''}
    </div>
  `;

  attachStep1Events();
  renderStep1FileInfo();
  renderFooter();
}

function renderStep1FileInfo() {
  const wrap = document.getElementById('p4-csv-file-info-wrap');
  if (!wrap) return;
  if (!modalState.file) {
    wrap.innerHTML = '';
    return;
  }
  const f = modalState.file;
  const sizeKb = (f.size / 1024).toFixed(1);
  wrap.innerHTML = `
    <div class="p4-csv-file-info">
      <strong>✓ 選択中</strong>
      <span>${escapeHtml(f.name)}</span>
      <span class="p4-csv-file-meta">${sizeKb} KB</span>
    </div>
  `;
}

function attachStep1Events() {
  const input = document.getElementById('p4-csv-file-input');
  const dropzone = document.getElementById('p4-csv-dropzone');
  const tmplLink = document.getElementById('p4-csv-template-link');

  input.addEventListener('change', (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) handleFileSelected(file);
  });

  // ドラッグ&ドロップ
  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('is-dragover');
  });
  dropzone.addEventListener('dragleave', () => {
    dropzone.classList.remove('is-dragover');
  });
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('is-dragover');
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  });

  tmplLink.addEventListener('click', (e) => {
    e.preventDefault();
    downloadTemplate();
  });
}

function handleFileSelected(file) {
  modalState.fileError = null;

  // 拡張子チェック(MIME より安定)
  if (!/\.csv$/i.test(file.name)) {
    modalState.fileError = '.csv ファイルを選択してください';
    modalState.file = null;
    renderStep1();
    return;
  }

  // サイズチェック
  if (file.size > MAX_FILE_SIZE_BYTES) {
    modalState.fileError = `ファイルサイズが大きすぎます(上限 ${MAX_FILE_SIZE_BYTES / 1024 / 1024} MB)`;
    modalState.file = null;
    renderStep1();
    return;
  }

  modalState.file = file;
  renderStep1();
}

function renderFooter() {
  const footer = document.getElementById('p4-csv-footer');
  if (!footer) return;

  if (modalState.step === 1) {
    footer.innerHTML = `
      <button type="button" class="btn btn-ghost" id="p4-csv-cancel-btn">キャンセル</button>
      <button type="button" class="btn btn-primary" id="p4-csv-next-btn"
              ${modalState.file ? '' : 'disabled'}>次へ →</button>
    `;
    document.getElementById('p4-csv-cancel-btn').addEventListener('click', handleCloseRequest);
    document.getElementById('p4-csv-next-btn').addEventListener('click', goToStep2);
    return;
  }

  if (modalState.step === 2) {
    const includedCount = modalState.parsedRows.filter((r) => isRowIncluded(r)).length;
    const canImport = includedCount > 0;
    footer.innerHTML = `
      <button type="button" class="btn btn-ghost" id="p4-csv-back-btn">← 戻る</button>
      <span style="flex:1;"></span>
      <button type="button" class="btn btn-primary" id="p4-csv-execute-btn"
              ${canImport ? '' : 'disabled'}>
        ${includedCount} 件を投入
      </button>
    `;
    document.getElementById('p4-csv-back-btn').addEventListener('click', () => {
      renderStep1();
    });
    document.getElementById('p4-csv-execute-btn').addEventListener('click', executeImport);
    return;
  }

  if (modalState.step === 3) {
    if (modalState.importResult) {
      footer.innerHTML = `
        <button type="button" class="btn btn-primary" id="p4-csv-done-btn">閉じる</button>
      `;
      document.getElementById('p4-csv-done-btn').addEventListener('click', () => {
        const cb = modalState.onComplete;
        closeModal();
        if (typeof cb === 'function') cb();
      });
    } else {
      // 投入中は閉じるボタンも出さない
      footer.innerHTML = '';
    }
  }
}

// =====================================================================
// Step 1 → Step 2 遷移:CSV パース + 検証
// =====================================================================
async function goToStep2() {
  if (!modalState.file) return;

  // 一旦 Step 1 の UI を消して読み込み中状態に
  const body = document.getElementById('p4-csv-body');
  body.innerHTML = '<div class="p4-loading" style="text-align:center;padding:60px;">読み込み中…</div>';

  try {
    const text = await readFileAsText(modalState.file);
    const { headers, rows } = parseCsv(text);

    // ヘッダー検証
    const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
    if (missing.length > 0) {
      modalState.fileError = `必須列が見つかりません: ${missing.join(', ')}`;
      renderStep1();
      return;
    }
    if (headers.length === 0) {
      modalState.fileError = 'CSV にヘッダー行が見つかりませんでした';
      renderStep1();
      return;
    }
    if (rows.length === 0) {
      modalState.fileError = 'CSV にデータ行が含まれていません';
      renderStep1();
      return;
    }

    // 全行を検証してプレビュー用の構造に変換
    const parsedRows = rows.map((rawRow, idx) => {
      const obj = headersToObject(headers, rawRow);
      return validateRow(obj, idx + 2);  // CSV 上の行番号(1=ヘッダー、2=最初のデータ行)
    });

    modalState.parsedRows = parsedRows;
    modalState.step = 2;
    renderStep2();
  } catch (err) {
    console.error('[チバリヨ P4 CSV] パース失敗:', err);
    modalState.fileError = 'CSV のパースに失敗しました: ' + err.message;
    renderStep1();
  }
}

// =====================================================================
// ファイル読み取り(UTF-8 / Shift_JIS 自動判定)
// =====================================================================
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const buffer = reader.result;
        const decoded = decodeAuto(buffer);
        resolve(decoded);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('ファイル読み取りに失敗しました'));
    reader.readAsArrayBuffer(file);
  });
}

function decodeAuto(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);

  // UTF-8 BOM チェック
  if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    const view = bytes.slice(3);
    return new TextDecoder('utf-8').decode(view);
  }

  // UTF-8 として fatal モードで試す(無効バイトがあれば例外)
  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return text;
  } catch (e) {
    // フォールバックとして Shift_JIS でデコード
    return new TextDecoder('shift_jis').decode(bytes);
  }
}

// =====================================================================
// CSV パース(RFC 4180 準拠の簡易実装)
//   - カンマ区切り
//   - ダブルクォートで囲まれたフィールド内のカンマ・改行・"" (エスケープ)に対応
//   - 末尾の改行は無視
// =====================================================================
function parseCsv(text) {
  // BOM が残っていたら除去(decodeAuto で取れているはずだが二重保険)
  if (text.charCodeAt(0) === 0xFEFF) {
    text = text.substring(1);
  }
  // CRLF → LF 正規化
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  const rows = [];
  let currentRow = [];
  let currentField = '';
  let inQuote = false;
  let i = 0;

  while (i < text.length) {
    const ch = text[i];

    if (inQuote) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          currentField += '"';
          i += 2;
          continue;
        } else {
          inQuote = false;
          i++;
          continue;
        }
      } else {
        currentField += ch;
        i++;
        continue;
      }
    } else {
      if (ch === '"') {
        inQuote = true;
        i++;
        continue;
      }
      if (ch === ',') {
        currentRow.push(currentField);
        currentField = '';
        i++;
        continue;
      }
      if (ch === '\n') {
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
        i++;
        continue;
      }
      currentField += ch;
      i++;
    }
  }

  // 末尾フィールド処理
  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField);
    rows.push(currentRow);
  }

  // 空行をスキップ(全列が空文字)
  const nonEmpty = rows.filter((r) => r.some((c) => c.trim() !== ''));
  if (nonEmpty.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = nonEmpty[0].map((h) => h.trim());
  const dataRows = nonEmpty.slice(1);

  return { headers, rows: dataRows };
}

// =====================================================================
// ヘッダー行とデータ行を組み合わせて { 列名: 値 } の形に
// =====================================================================
function headersToObject(headers, row) {
  const obj = {};
  for (let i = 0; i < headers.length; i++) {
    const key = headers[i];
    if (!CSV_HEADERS.includes(key)) continue;  // 未知の列は無視
    obj[key] = (row[i] || '').trim();
  }
  return obj;
}

// =====================================================================
// 1行ぶんの検証
//   戻り値: {
//     rowNum, raw, parsed,
//     issues: [{ level: 'error'|'warn', col, message }],
//     status: 'ok' | 'warn' | 'error' | 'duplicate',
//     isDuplicate: bool, matchedExistingId: string|null,
//     action: 'create' | 'skip' | 'overwrite',
//     include: bool,
//     resolvedMemberId: string|null
//   }
// =====================================================================
function validateRow(obj, rowNum) {
  const issues = [];

  const name = (obj['顧客名'] || '').trim();
  const nameKana = (obj['カナ'] || '').trim();
  const prefecture = (obj['都道府県'] || '').trim();
  const municipality = (obj['市町村'] || '').trim();
  const businessTypesRaw = (obj['提案種別'] || '').trim();
  const primaryMemberName = (obj['主担当'] || '').trim();
  const category = (obj['カテゴリ'] || '').trim();
  const notes = (obj['備考'] || '').trim();

  // 必須:顧客名
  if (!name) {
    issues.push({ level: 'error', col: '顧客名', message: '顧客名は必須です' });
  } else if (name.length > 100) {
    issues.push({ level: 'error', col: '顧客名', message: '顧客名は100文字以内' });
  }

  // カナ
  if (nameKana) {
    if (nameKana.length > 100) {
      issues.push({ level: 'error', col: 'カナ', message: 'カナは100文字以内' });
    } else if (!/^[\u30A0-\u30FFーぁ-んー・\s]*$/.test(nameKana)) {
      // カタカナ + 長音 + 中点 + 空白 + ひらがなも一応許容
      // 実用上は緩めにしておく(漢字が混ざっていたら警告)
      if (/[\u4E00-\u9FFF]/.test(nameKana)) {
        issues.push({ level: 'warn', col: 'カナ', message: 'カナに漢字が含まれています' });
      }
    }
  }

  // 都道府県
  const validPrefs = getValidPrefectures();
  if (prefecture && !validPrefs.includes(prefecture)) {
    issues.push({ level: 'warn', col: '都道府県', message: `「${prefecture}」は47都道府県と一致しません` });
  }

  // 市町村(municipalitiesData が読めている場合のみ厳密チェック)
  if (municipality && prefecture) {
    const mdata = modalState.municipalitiesData;
    if (mdata && mdata[prefecture] && !mdata[prefecture].includes(municipality)) {
      issues.push({
        level: 'warn',
        col: '市町村',
        message: `「${municipality}」は「${prefecture}」の市町村マスタにありません`
      });
    }
  }

  // 提案種別
  const businessTypes = [];
  const businessTypesUnknown = [];
  if (businessTypesRaw) {
    const items = businessTypesRaw.split(',').map((s) => s.trim()).filter(Boolean);
    for (const item of items) {
      if (BUSINESS_TYPES.includes(item)) {
        if (!businessTypes.includes(item)) businessTypes.push(item);
      } else {
        businessTypesUnknown.push(item);
      }
    }
    if (businessTypesUnknown.length > 0) {
      issues.push({
        level: 'warn',
        col: '提案種別',
        message: `未知の提案種別は無視されます: ${businessTypesUnknown.join(', ')}`
      });
    }
  }

  // 主担当
  let resolvedMemberId = null;
  if (primaryMemberName) {
    const matched = findMemberByName(primaryMemberName);
    if (matched) {
      resolvedMemberId = matched.id;
    } else {
      issues.push({
        level: 'warn',
        col: '主担当',
        message: `「${primaryMemberName}」がメンバーに見つかりません(未割当として投入されます)`
      });
    }
  }

  // カテゴリ
  if (category && category.length > 50) {
    issues.push({ level: 'error', col: 'カテゴリ', message: 'カテゴリは50文字以内' });
  }

  // 備考
  if (notes && notes.length > 1000) {
    issues.push({ level: 'error', col: '備考', message: '備考は1000文字以内' });
  }

  // 重複検出(エラー行は重複判定スキップ)
  let isDuplicate = false;
  let matchedExistingId = null;
  const hasError = issues.some((iss) => iss.level === 'error');
  if (!hasError && name) {
    const key = normalizeClientName(name);
    if (key) {
      const match = modalState.existingClients.find((c) => {
        if (c.active === false) return false;
        return normalizeClientName(c.name || '') === key;
      });
      if (match) {
        isDuplicate = true;
        matchedExistingId = match.id;
      }
    }
  }

  // ステータス決定
  let status;
  if (hasError) status = 'error';
  else if (isDuplicate) status = 'duplicate';
  else if (issues.some((iss) => iss.level === 'warn')) status = 'warn';
  else status = 'ok';

  // 既定アクション
  let action;
  if (status === 'error') action = 'skip';
  else if (status === 'duplicate') action = 'skip';  // ポリシーに応じて後で書き換え
  else action = 'create';

  return {
    rowNum,
    raw: obj,
    parsed: {
      name, nameKana, prefecture, municipality,
      businessTypes,
      primaryMemberName,
      resolvedMemberId,
      category, notes
    },
    issues,
    status,
    isDuplicate,
    matchedExistingId,
    action,
    include: status !== 'error',  // エラー以外は既定で投入対象
    resolvedMemberId
  };
}

function getValidPrefectures() {
  if (modalState.municipalitiesData) {
    return Object.keys(modalState.municipalitiesData);
  }
  return PREFECTURES_FALLBACK;
}

function findMemberByName(name) {
  if (!name) return null;
  const trimmed = name.trim();
  // members は Map<id, memberData> もしくは配列で渡されうる
  const arr = (modalState.members instanceof Map)
    ? Array.from(modalState.members.values())
    : (Array.isArray(modalState.members) ? modalState.members : []);
  return arr.find((m) => (m.name || '').trim() === trimmed && m.active !== false) || null;
}

function isRowIncluded(row) {
  if (!row.include) return false;
  if (row.status === 'error') return false;
  if (row.isDuplicate && row.action === 'skip') return false;
  return true;
}

// =====================================================================
// Step 2: プレビュー & 検証
// =====================================================================
function renderStep2() {
  modalState.step = 2;
  renderSteps();
  applyDuplicatePolicy();  // 既定ポリシーを反映

  const body = document.getElementById('p4-csv-body');
  body.innerHTML = `
    <div class="p4-csv-step2">
      <div id="p4-csv-summary-wrap"></div>
      <div id="p4-csv-dup-policy-wrap"></div>
      <div class="p4-csv-table-wrapper">
        <table class="p4-csv-table" id="p4-csv-table">
          <thead>
            <tr>
              <th class="p4-csv-col-include" title="投入対象">投入</th>
              <th class="p4-csv-col-status">状態</th>
              <th class="p4-csv-col-row-num">行</th>
              <th>顧客名</th>
              <th>カナ</th>
              <th>エリア</th>
              <th>提案種別</th>
              <th>主担当</th>
              <th>カテゴリ</th>
              <th>備考</th>
              <th class="p4-csv-col-action">処理</th>
            </tr>
          </thead>
          <tbody id="p4-csv-table-body"></tbody>
        </table>
      </div>
    </div>
  `;

  renderSummary();
  renderDupPolicyBar();
  renderRows();
  renderFooter();
}

function renderSummary() {
  const counts = countByStatus();
  const wrap = document.getElementById('p4-csv-summary-wrap');
  if (!wrap) return;
  const total = modalState.parsedRows.length;
  wrap.innerHTML = `
    <div class="p4-csv-summary">
      <div class="p4-csv-summary-item">
        <span class="p4-csv-summary-label">CSV</span>
        <span class="p4-csv-summary-value">${total}</span>
        <span class="p4-csv-summary-label">行</span>
      </div>
      <div class="p4-csv-summary-divider"></div>
      <div class="p4-csv-summary-item is-ok">
        <span class="p4-csv-summary-label">✅ 正常</span>
        <span class="p4-csv-summary-value">${counts.ok}</span>
      </div>
      <div class="p4-csv-summary-item is-warn">
        <span class="p4-csv-summary-label">⚠ 警告</span>
        <span class="p4-csv-summary-value">${counts.warn}</span>
      </div>
      <div class="p4-csv-summary-item is-error">
        <span class="p4-csv-summary-label">❌ エラー</span>
        <span class="p4-csv-summary-value">${counts.error}</span>
      </div>
      <div class="p4-csv-summary-item is-duplicate">
        <span class="p4-csv-summary-label">🔁 重複</span>
        <span class="p4-csv-summary-value">${counts.duplicate}</span>
      </div>
    </div>
  `;
}

function renderDupPolicyBar() {
  const wrap = document.getElementById('p4-csv-dup-policy-wrap');
  if (!wrap) return;
  const dupCount = modalState.parsedRows.filter((r) => r.isDuplicate).length;
  if (dupCount === 0) {
    wrap.innerHTML = '';
    return;
  }
  wrap.innerHTML = `
    <div class="p4-csv-dup-policy">
      <span class="p4-csv-dup-policy-label">重複 ${dupCount} 件の既定処理:</span>
      <label>
        <input type="radio" name="p4-csv-duppolicy" value="skip_all"
               ${modalState.duplicatePolicy === 'skip_all' ? 'checked' : ''}>
        全てスキップ(既存を維持)
      </label>
      <label>
        <input type="radio" name="p4-csv-duppolicy" value="overwrite_all"
               ${modalState.duplicatePolicy === 'overwrite_all' ? 'checked' : ''}>
        全て上書き(CSV を反映)
      </label>
      <label>
        <input type="radio" name="p4-csv-duppolicy" value="individual"
               ${modalState.duplicatePolicy === 'individual' ? 'checked' : ''}>
        個別選択
      </label>
    </div>
  `;
  document.querySelectorAll('input[name="p4-csv-duppolicy"]').forEach((el) => {
    el.addEventListener('change', (e) => {
      modalState.duplicatePolicy = e.target.value;
      applyDuplicatePolicy();
      renderRows();
      renderFooter();
    });
  });
}

function applyDuplicatePolicy() {
  for (const row of modalState.parsedRows) {
    if (!row.isDuplicate) continue;
    if (modalState.duplicatePolicy === 'skip_all') row.action = 'skip';
    else if (modalState.duplicatePolicy === 'overwrite_all') row.action = 'overwrite';
    // individual は触らない
  }
}

function countByStatus() {
  const c = { ok: 0, warn: 0, error: 0, duplicate: 0 };
  for (const row of modalState.parsedRows) {
    c[row.status] = (c[row.status] || 0) + 1;
  }
  return c;
}

function renderRows() {
  const tbody = document.getElementById('p4-csv-table-body');
  if (!tbody) return;
  const rows = modalState.parsedRows;
  if (rows.length === 0) {
    tbody.innerHTML = '<tr><td colspan="11" style="text-align:center;padding:24px;color:var(--color-text-muted);">データ行がありません</td></tr>';
    return;
  }
  tbody.innerHTML = rows.map((row) => renderRow(row)).join('');
  attachRowEvents();
}

function renderRow(row) {
  const p = row.parsed;
  const statusIcon = {
    ok: '✅', warn: '⚠', error: '❌', duplicate: '🔁'
  }[row.status] || '';
  const rowClasses = [
    'p4-csv-row-' + row.status,
    row.include ? '' : 'p4-csv-row-excluded'
  ].filter(Boolean).join(' ');

  const area = generateArea(p.prefecture, p.municipality) || '<span class="p4-csv-cell-sub">—</span>';
  const includeCheckbox = (row.status === 'error')
    ? '<input type="checkbox" disabled>'
    : `<input type="checkbox" data-row="${row.rowNum}" class="p4-csv-include-cb"
              ${row.include ? 'checked' : ''}>`;

  // 提案種別バッジ
  const btBadges = p.businessTypes.map((bt) =>
    `<span class="p4-csv-bt-badge">${escapeHtml(bt)}</span>`
  ).join('');

  // 主担当の表示(逆引きできたかどうかで色変え)
  let memberDisplay = '<span class="p4-csv-cell-sub">—</span>';
  if (p.primaryMemberName) {
    if (p.resolvedMemberId) {
      memberDisplay = escapeHtml(p.primaryMemberName);
    } else {
      memberDisplay = `<span style="color:#854F0B;">${escapeHtml(p.primaryMemberName)} ?</span>`;
    }
  }

  // 処理列(重複行のみラジオ表示)
  let actionCell = '';
  if (row.isDuplicate) {
    actionCell = `
      <label>
        <input type="radio" name="p4-csv-action-${row.rowNum}"
               value="skip" data-row="${row.rowNum}"
               ${row.action === 'skip' ? 'checked' : ''}>
        スキップ
      </label>
      <label>
        <input type="radio" name="p4-csv-action-${row.rowNum}"
               value="overwrite" data-row="${row.rowNum}"
               ${row.action === 'overwrite' ? 'checked' : ''}>
        上書き
      </label>
    `;
  } else if (row.status === 'error') {
    actionCell = '<span class="p4-csv-cell-sub">スキップ</span>';
  } else {
    actionCell = '<span class="p4-csv-cell-sub">新規追加</span>';
  }

  // issue 表示
  const errors = row.issues.filter((i) => i.level === 'error');
  const warnings = row.issues.filter((i) => i.level === 'warn');
  const issuesHtml = (errors.length > 0 || warnings.length > 0) ? `
    <div class="p4-csv-issues">
      ${[...errors, ...warnings].map((iss) =>
        `<div>${iss.level === 'error' ? '❌' : '⚠'} ${escapeHtml(iss.col)}: ${escapeHtml(iss.message)}</div>`
      ).join('')}
    </div>
  ` : '';

  // 顧客名セル(正規化キーも併記)
  const normalized = normalizeClientName(p.name);
  const nameCell = `
    ${escapeHtml(p.name) || '<span class="p4-csv-cell-sub">(空)</span>'}
    ${normalized && normalized !== p.name ? `<span class="p4-csv-cell-sub">正規化: ${escapeHtml(normalized)}</span>` : ''}
    ${row.isDuplicate ? `<span class="p4-csv-cell-sub">↳ 既存と重複</span>` : ''}
    ${issuesHtml}
  `;

  return `
    <tr class="${rowClasses}" data-row="${row.rowNum}">
      <td class="p4-csv-col-include">${includeCheckbox}</td>
      <td class="p4-csv-col-status">${statusIcon}</td>
      <td class="p4-csv-col-row-num">${row.rowNum}</td>
      <td>${nameCell}</td>
      <td>${escapeHtml(p.nameKana) || '<span class="p4-csv-cell-sub">—</span>'}</td>
      <td>${area}</td>
      <td>${btBadges || '<span class="p4-csv-cell-sub">—</span>'}</td>
      <td>${memberDisplay}</td>
      <td>${escapeHtml(p.category) || '<span class="p4-csv-cell-sub">—</span>'}</td>
      <td>${escapeHtml((p.notes || '').substring(0, 40))}${(p.notes || '').length > 40 ? '…' : ''}</td>
      <td class="p4-csv-col-action">${actionCell}</td>
    </tr>
  `;
}

function attachRowEvents() {
  document.querySelectorAll('.p4-csv-include-cb').forEach((cb) => {
    cb.addEventListener('change', (e) => {
      const rowNum = parseInt(e.target.dataset.row, 10);
      const row = modalState.parsedRows.find((r) => r.rowNum === rowNum);
      if (!row) return;
      row.include = e.target.checked;
      renderSummary();
      renderRows();
      renderFooter();
    });
  });

  document.querySelectorAll('input[name^="p4-csv-action-"]').forEach((el) => {
    el.addEventListener('change', (e) => {
      const rowNum = parseInt(e.target.dataset.row, 10);
      const row = modalState.parsedRows.find((r) => r.rowNum === rowNum);
      if (!row) return;
      row.action = e.target.value;
      // 個別操作されたので、ポリシーを「個別選択」に切り替える
      if (modalState.duplicatePolicy !== 'individual') {
        modalState.duplicatePolicy = 'individual';
        renderDupPolicyBar();
      }
      renderFooter();
    });
  });
}

// =====================================================================
// Step 3: 投入実行
// =====================================================================
async function executeImport() {
  modalState.step = 3;
  modalState.importResult = null;
  renderSteps();
  renderStep3Progress();
  renderFooter();

  const includedRows = modalState.parsedRows.filter(isRowIncluded);
  if (includedRows.length === 0) {
    modalState.importResult = {
      created: 0, overwritten: 0, skipped: modalState.parsedRows.length, failed: 0,
      failedRows: []
    };
    renderStep3Result();
    renderFooter();
    return;
  }

  // バッチサイズ単位で分割(256社なら通常1バッチで足りる)
  const batches = chunk(includedRows, FIRESTORE_BATCH_LIMIT);
  let created = 0;
  let overwritten = 0;
  const failedRows = [];
  let processed = 0;

  updateProgress(0, includedRows.length);

  for (const batchRows of batches) {
    try {
      const batch = writeBatch(db);
      for (const row of batchRows) {
        const payload = buildPayload(row);
        const targetId = (row.action === 'overwrite' && row.matchedExistingId)
          ? row.matchedExistingId
          : generateClientId();
        const ref = doc(db, 'clients', targetId);
        if (row.action === 'overwrite') {
          batch.set(ref, payload.overwritePayload, { merge: true });
        } else {
          batch.set(ref, payload.createPayload);
        }
      }
      await batch.commit();
      // バッチ成功 → カウント加算
      for (const row of batchRows) {
        if (row.action === 'overwrite') overwritten++;
        else created++;
      }
      processed += batchRows.length;
      updateProgress(processed, includedRows.length);
    } catch (err) {
      console.error('[チバリヨ P4 CSV] バッチコミット失敗:', err);
      // バッチ全体が失敗(Firestore は atomic)
      for (const row of batchRows) {
        failedRows.push({ ...row, failureReason: err.message });
      }
      processed += batchRows.length;
      updateProgress(processed, includedRows.length);
    }
  }

  const skipped = modalState.parsedRows.length - includedRows.length;
  modalState.importResult = {
    created, overwritten, skipped, failed: failedRows.length, failedRows
  };
  renderStep3Result();
  renderFooter();
}

function buildPayload(row) {
  const p = row.parsed;
  const name = p.name.trim();
  const area = generateArea(p.prefecture, p.municipality);
  const nameNormalized = normalizeClientName(name);
  const isPartiallyFilled = !p.nameKana
    || (!p.prefecture && !p.municipality)
    || p.businessTypes.length === 0;

  const sessionId = modalState.currentSessionMember?.id || null;

  // 共通フィールド
  const common = {
    name,
    nameKana: p.nameKana,
    nameNormalized,
    prefecture: p.prefecture,
    municipality: p.municipality,
    area,
    category: p.category,
    businessTypes: [...p.businessTypes],
    primaryMemberId: p.resolvedMemberId || null,
    notes: p.notes,
    isPartiallyFilled,
    updatedAt: serverTimestamp()
  };

  // create 用:訪問記録など初期値を全て付ける
  const createPayload = {
    ...common,
    active: true,
    visitCount: 0,
    lastVisitDate: null,
    mergedInto: null,
    archivedAt: null,
    archivedBy: null,
    archivedReason: '',
    createdAt: serverTimestamp(),
    createdBy: sessionId
  };

  // overwrite 用:書き換えていいものだけ(訪問記録・archive・createdAt は触らない)
  const overwritePayload = { ...common };

  return { createPayload, overwritePayload };
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

function generateClientId() {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 8);
  return `cli_${ts}_${rand}`;
}

function renderStep3Progress() {
  const body = document.getElementById('p4-csv-body');
  body.innerHTML = `
    <div class="p4-csv-step3">
      <div class="p4-csv-progress-wrap">
        <div class="p4-csv-progress-label" id="p4-csv-progress-label">投入中… 0 / 0</div>
        <div class="p4-csv-progress-bar"><div class="p4-csv-progress-fill" id="p4-csv-progress-fill"></div></div>
        <div class="p4-csv-progress-warn">⚠ この画面を閉じないでください</div>
      </div>
    </div>
  `;
}

function updateProgress(done, total) {
  const label = document.getElementById('p4-csv-progress-label');
  const fill = document.getElementById('p4-csv-progress-fill');
  if (label) label.textContent = `投入中… ${done} / ${total}`;
  if (fill) {
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    fill.style.width = pct + '%';
  }
}

function renderStep3Result() {
  const r = modalState.importResult;
  const body = document.getElementById('p4-csv-body');
  const hasFailed = r.failed > 0;

  // ステップインジケータを再描画(失敗0件なら Step 3 が緑化される)
  renderSteps();

  body.innerHTML = `
    <div class="p4-csv-step3">
      <div class="p4-csv-result">
        <div class="p4-csv-result-title ${hasFailed ? 'is-error' : ''}">
          ${hasFailed ? '⚠ 一部失敗' : '✅ 投入完了'}
        </div>
        <div class="p4-csv-result-row is-ok">
          <span class="p4-csv-result-label">✅ 新規追加</span>
          <span class="p4-csv-result-value">${r.created} 件</span>
        </div>
        <div class="p4-csv-result-row ${r.overwritten > 0 ? 'is-ok' : ''}">
          <span class="p4-csv-result-label">🔁 上書き更新</span>
          <span class="p4-csv-result-value">${r.overwritten} 件</span>
        </div>
        <div class="p4-csv-result-row is-skip">
          <span class="p4-csv-result-label">⏭ スキップ</span>
          <span class="p4-csv-result-value">${r.skipped} 件</span>
        </div>
        <div class="p4-csv-result-row ${r.failed > 0 ? 'is-error' : ''}">
          <span class="p4-csv-result-label">❌ 失敗</span>
          <span class="p4-csv-result-value">${r.failed} 件</span>
        </div>
        ${r.failed > 0 ? `
          <div class="p4-csv-result-download">
            <a href="#" id="p4-csv-download-failed">📥 失敗行 CSV をダウンロード</a>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  if (r.failed > 0) {
    document.getElementById('p4-csv-download-failed').addEventListener('click', (e) => {
      e.preventDefault();
      downloadFailedRows(r.failedRows);
    });
  }
}

// =====================================================================
// テンプレ CSV ダウンロード
// =====================================================================
function downloadTemplate() {
  // public/data/clients_template.csv を fetch して保存 を促す(rewrites 影響を受けない)
  const link = document.createElement('a');
  link.href = '/data/clients_template.csv';
  link.download = 'clients_template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// =====================================================================
// 失敗行 CSV ダウンロード
// =====================================================================
function downloadFailedRows(failedRows) {
  const headers = [...CSV_HEADERS, '失敗理由'];
  const rows = failedRows.map((row) => {
    const p = row.parsed;
    return [
      p.name,
      p.nameKana,
      p.prefecture,
      p.municipality,
      p.businessTypes.join(','),
      p.primaryMemberName,
      p.category,
      p.notes,
      row.failureReason || ''
    ];
  });

  const csv = buildCsvText(headers, rows);
  // UTF-8 BOM 付きで Excel での文字化け防止
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const link = document.createElement('a');
  link.href = url;
  link.download = `clients_failed_${ts}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function buildCsvText(headers, rows) {
  const escapeCell = (v) => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const lines = [
    headers.map(escapeCell).join(','),
    ...rows.map((r) => r.map(escapeCell).join(','))
  ];
  return lines.join('\r\n') + '\r\n';
}

// =====================================================================
// p4-clients.js と同一の normalizeClientName
//   全角→半角、英字小文字化、空白・記号類を除去、法人格を除去
// =====================================================================
function normalizeClientName(name) {
  if (!name) return '';

  let s = name
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\s\u3000・/／\-―()()「」『』、,.。·]/g, '');

  const LEGAL_PREFIXES = [
    '特定非営利活動法人', '一般社団法人', '公益社団法人', '一般財団法人', '公益財団法人',
    '地方独立行政法人', '独立行政法人', '国立大学法人',
    '社会福祉法人', '医療法人', '学校法人', '宗教法人', '相互会社',
    '社団法人', '財団法人', 'npo法人',
    '農業協同組合', '漁業協同組合', '生活協同組合', '事業協同組合', '協同組合',
    '株式会社', '有限会社', '合同会社', '合資会社', '合名会社',
    '株', '有', '同'
  ];

  for (const p of LEGAL_PREFIXES) {
    if (s.startsWith(p)) {
      s = s.substring(p.length);
      break;
    }
  }
  for (const p of LEGAL_PREFIXES) {
    if (s.endsWith(p)) {
      s = s.substring(0, s.length - p.length);
      break;
    }
  }

  return s.trim();
}

// =====================================================================
// p4-clients.js と同一の generateArea
// =====================================================================
function generateArea(prefecture, municipality) {
  if (!prefecture && !municipality) return '';
  if (!municipality) return prefecture;
  if (!prefecture) return municipality;
  return prefecture + municipality;
}

// =====================================================================
// ユーティリティ
// =====================================================================
function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
